
import { useState } from 'react';
import { TeacherFormData, TeacherDocument } from '@/types/teacher.types';
import { teacherFileStorageService } from '@/services/db/teachers/fileStorageService';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export const useTeacherUploads = (
  formData: TeacherFormData,
  setFormData: React.Dispatch<React.SetStateAction<TeacherFormData>>
) => {
  const [isUploading, setIsUploading] = useState(false);

  const compressImage = async (base64String: string): Promise<string> => {
    // Simple compression by reducing quality if image is too large
    if (base64String.length > 500000) { // If larger than ~500KB
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Reduce dimensions if too large
          const maxWidth = 800;
          const maxHeight = 800;
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx?.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.src = base64String;
      });
    }
    return base64String;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      console.log('📤 useTeacherUploads: Starting image upload process...');
      
      // Convert file to base64
      const base64String = await fileToBase64(file);
      console.log('🔄 useTeacherUploads: Image converted to base64, length:', base64String.length);
      
      const compressedImage = await compressImage(base64String);
      console.log('🗜️ useTeacherUploads: Image compressed, final length:', compressedImage.length);
      
      // Update form data with the compressed image directly
      setFormData(prev => {
        console.log('💾 useTeacherUploads: Updating formData with profileImage');
        return {
          ...prev,
          profileImage: compressedImage
        };
      });
      
      toast.success('Profile image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const setProfileImage = (imageUrl: string) => {
    console.log('🖼️ useTeacherUploads: setProfileImage called with:', imageUrl.substring(0, 50) + '...');
    setFormData(prev => ({
      ...prev,
      profileImage: imageUrl
    }));
  };

  const handleDocumentUpload = async (files: File[], documentType: string = 'general') => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Document ${file.name} size should be less than 10MB`);
          return null;
        }

        const base64String = await fileToBase64(file);
        
        // Create document reference directly without external media storage
        const newDocument: TeacherDocument = {
          id: uuidv4(),
          name: file.name,
          size: file.size,
          type: file.type,
          url: base64String, // Store base64 directly
          documentType: documentType as any,
          dateAdded: new Date().toISOString(),
          status: 'valid'
        };

        return newDocument;
      });

      const uploadedDocuments = (await Promise.all(uploadPromises)).filter(doc => doc !== null);
      
      if (uploadedDocuments.length > 0) {
        setFormData(prev => ({
          ...prev,
          documents: [...(prev.documents || []), ...uploadedDocuments]
        }));
        
        toast.success(`${uploadedDocuments.length} document(s) uploaded successfully`);
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Failed to upload documents');
    } finally {
      setIsUploading(false);
    }
  };

  const viewDocument = (document: TeacherDocument) => {
    try {
      if (document.url) {
        // Create a blob URL for viewing
        if (document.url.startsWith('data:')) {
          // It's already a base64 data URL, open directly
          window.open(document.url, '_blank');
        } else {
          // Try to create blob from base64
          const byteCharacters = atob(document.url.split(',')[1] || document.url);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: document.type });
          const url = URL.createObjectURL(blob);
          
          window.open(url, '_blank');
          
          // Clean up the blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
      } else {
        toast.error('Document data not found');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to open document');
    }
  };

  return {
    handleImageUpload,
    setProfileImage,
    handleDocumentUpload,
    viewDocument,
    isUploading
  };
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
